import PropTypes from 'prop-types';
import { Markup } from 'interweave';
import React, { useState } from 'react';
import Button from '@strava/ui/Button';
import createNetworkingClient from 'utils/networking-client';
import I18n from 'utils/I18n';
import styles from './index.scss';
import { useFeedEntityContext } from '../../../state/FeedEntityContext';
import {
  indexProps,
  cursorDataProps,
  viewingAthleteProps
} from '../../../props/feed-prop-types';

import FeedEntryAgeGate from '../../../../challenges/FeedEntryAgeGate';

const AthleteJoinEntry = ({
  entity,
  entityId,
  title,
  badge,
  entityName,
  entityUrl,
  entityIcon,
  entityMemberCount,
  isMember,
  secondaryText,
  intersectionObserverRef,
  cursorData,
  index,
  minimumAge,
  joinMethod,
  viewingAthlete,
  labels
}) => {
  const { entityState } = useFeedEntityContext();
  const isClub = () => entity === 'Club';

  const cardMargin = index > 0 ? styles.cardMargin : '';

  const buttonLabelAndVariant = isMember
    ? [labels.join, 'default']
    : [labels.view, 'primary'];

  const [entryState, setEntryState] = useState({
    memberCountLabel: I18n.t(labels.memberCountKey, {
      count: entityMemberCount
    }),
    disabled: false, // controls whether the join button is enabled/disabled
    label: buttonLabelAndVariant[0], // join button label text
    variant: buttonLabelAndVariant[1], // join button variant
    openAgeGateModal: false,
    ageGateModalProps: {}
  });

  // joins either a challenge or a club and increments the participant/membership count
  const join = ({ url, payload }) => {
    createNetworkingClient()
      .post(url, payload, { headers: { Accept: 'text/javascript' } })
      .then(() => {
        setEntryState({
          label: labels.joined,
          variant: 'primary',
          disabled: true,
          memberCountLabel: I18n.t(labels.memberCountKey, {
            count: entityMemberCount + 1
          })
        });
      })
      .catch(() => {
        // TODO: [fumba] log error in monorepo
        setEntryState((prev) => ({
          ...prev,
          label: labels.error,
          disabled: false
        }));
      });
  };

  // Checks if the athlete qualifies for any age-gate requirements that might be present
  // Athletes are joined in the challenge if they meet the criteria otherwise an age-gating modal
  // is opened prompting further action.
  const ageGateAndJoinChallenge = () => {
    const youngestDob =
      minimumAge &&
      (() => {
        const d = new Date(new Date().toDateString());
        d.setFullYear(d.getFullYear() - parseInt(minimumAge, 10));
        return d;
      })();
    if (
      minimumAge &&
      (!viewingAthlete.dateOfBirth ||
        new Date(viewingAthlete.dateOfBirth) > youngestDob)
    ) {
      const ageGateModalProps = {
        minimumAge: parseInt(minimumAge, 10),
        joinUrl: entityUrl,
        redirectUrl: `/challenges/${entityId}#joined`,
        joinMethod,
        currentAthlete: {
          id: parseInt(viewingAthlete.id, 10),
          dateOfBirth: viewingAthlete.dateOfBirth
        }
      };
      // open age-gate modal
      setEntryState((prev) => ({
        ...prev,
        disabled: true,
        openAgeGateModal: true,
        ageGateModalProps
      }));
    } else {
      // viewing athlete passed age-gating requirements - join them in challenge
      setEntryState((prev) => ({
        ...prev,
        disabled: true
      }));
      const payload = { challenge_id: entityId };
      const url = `/challenges/${entityId}/join`;
      join({ payload, url });
    }
  };

  const onJoinButtonClick = () => {
    if (isMember) {
      // Join button is used to navigate to club/challenge detail page if the athlete is alredy joined
      entityState.trackAnalytics({
        action: 'click',
        element: `view_${entity.toLowerCase()}`
      });
      window.location.href = entityUrl;
      return;
    }
    entityState.trackAnalytics({
      action: 'click',
      element: `join_${entity.toLowerCase()}`
    });

    setEntryState((prev) => ({
      ...prev,
      disabled: true
    }));
    if (isClub()) {
      // Join Club
      const payload = { club_id: entityId };
      const url = `/clubs/${entityId}/members`;
      join({ payload, url });
    } else {
      // Challenge - age gate check & Join if applicable
      ageGateAndJoinChallenge();
    }
  };

  const clickGroupHeader = () => {
    entityState.trackAnalytics({
      action: 'click',
      element: 'group_header',
      properties: { element_source: entity.toLowerCase() }
    });
  };

  const clickTitleText = () => {
    entityState.trackAnalytics({
      action: 'click',
      element: 'title',
      properties: { element_source: entity.toLowerCase() }
    });
  };

  return (
    /* react-feed-entry is being used by feed3 container for pagination - https://github.com/strava/active/blob/main/app/assets/javascripts/strava/dashboard/PaginationView.js.coffee#L52-L73 */
    <div
      className={`react-feed-entry ${styles.feedEntry} ${styles.card} ${cardMargin}`}
      data-updated-at={cursorData.updated_at}
      data-rank={cursorData.rank}
      data-testid="web-feed-entry"
      ref={intersectionObserverRef}
    >
      <div className={styles.entryHeader}>
        <div
          className={styles.entryOwner}
          onClick={clickGroupHeader}
          onKeyDown={clickGroupHeader}
          data-testid="group-header"
          role="link"
          tabIndex="-1"
        >
          <Markup noWrap={true} content={title} />
        </div>
      </div>

      <div className={styles.entryBody}>
        <div className={styles.media}>
          <div className={styles.mediaLeft}>{badge}</div>
          <div className={styles.mediaBody}>
            <h3 className={styles.titleText}>
              <a
                href={entityUrl}
                onClick={clickTitleText}
                data-testid="title-text"
              >
                {entityName}
              </a>
            </h3>
            <div className={styles.details}>
              {entityIcon && (
                <div className={styles.iconWrapper}>{entityIcon}</div>
              )}
              <div className={styles.entryText}>
                <span>{entryState.memberCountLabel}</span>
                {secondaryText && <span>{secondaryText}</span>}
              </div>
            </div>

            <Button
              onClick={onJoinButtonClick}
              variant={entryState.variant}
              disabled={entryState.disabled}
              className={styles.joinBtn}
              type="button"
            >
              {entryState.label}
            </Button>
          </div>
        </div>
      </div>

      {entryState.openAgeGateModal && (
        <FeedEntryAgeGate {...entryState.ageGateModalProps} />
      )}
    </div>
  );
};

AthleteJoinEntry.propTypes = {
  entity: PropTypes.oneOf(['Club', 'Challenge']).isRequired,
  entityId: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  badge: PropTypes.node.isRequired,
  entityName: PropTypes.string.isRequired,
  entityUrl: PropTypes.string.isRequired,
  entityIcon: PropTypes.node,
  entityMemberCount: PropTypes.number.isRequired,
  isMember: PropTypes.bool.isRequired,
  secondaryText: PropTypes.string.isRequired,
  intersectionObserverRef: PropTypes.func.isRequired,
  cursorData: cursorDataProps.isRequired,
  index: indexProps.isRequired,
  minimumAge: PropTypes.number,
  joinMethod: PropTypes.string,
  viewingAthlete: viewingAthleteProps,
  labels: PropTypes.shape({
    join: PropTypes.string.isRequired,
    view: PropTypes.string.isRequired,
    joined: PropTypes.string.isRequired,
    memberCountKey: PropTypes.string.isRequired,
    error: PropTypes.string
  }).isRequired
};

AthleteJoinEntry.defaultProps = {
  entityIcon: null,
  minimumAge: null,
  joinMethod: '',
  viewingAthlete: null
};

export default AthleteJoinEntry;
